import React, { useState, useEffect, useCallback } from 'react';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { SetIcon } from 'components/SetIcon';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { EditableTradeItem } from 'store/services/trade/tradeApi';
import { TradeItem } from '../ActiveTradesPage';
import { formatLanguage } from 'constants/languages';

// Format condition to short acronym
const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM', 'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP', 'LIGHTLY_PLAYED': 'LP', 'LP': 'LP', 'SP': 'LP',
    'MODERATELY_PLAYED': 'MP', 'MP': 'MP',
    'HEAVILY_PLAYED': 'HP', 'HP': 'HP',
    'DAMAGED': 'DMG', 'DMG': 'DMG',
  };
  return conditionMap[condition] || condition;
};

// All possible conditions
const ALL_CONDITIONS = [
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
];

interface EditValues {
  cardId: number;
  setCode: string;
  setName: string;
  condition: string;
  language: string;
  foil: boolean;
  imageUrl: string;
  availableInFoil: boolean;
  availableInNonFoil: boolean;
}

interface Props {
  item: TradeItem;
  itemIndex: number;
  editOptions: EditableTradeItem;
  onSave: (itemId: string, updates: { cardId: number; condition: string; language: string; foil: boolean }) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export default function TradeItemEditRow({ item, itemIndex, editOptions, onSave, onCancel, isSaving }: Props) {
  const prefs = editOptions.preferences!;
  const [calculatePrice] = useCalculatePriceMutation();

  // Initialize edit values from the current item
  const [editValues, setEditValues] = useState<EditValues>(() => {
    const currentSetDetail = prefs.acceptableSetDetails.find(s => s.cardId === editOptions.cardId);
    return {
      cardId: editOptions.cardId,
      setCode: item.setCode || currentSetDetail?.code || '',
      setName: item.set,
      condition: editOptions.currentCondition,
      language: editOptions.currentLanguage,
      foil: editOptions.currentFoil,
      imageUrl: item.image,
      availableInFoil: currentSetDetail?.availableInFoil ?? false,
      availableInNonFoil: currentSetDetail?.availableInNonFoil ?? true,
    };
  });

  const [previewPrice, setPreviewPrice] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const originalPrice = editOptions.currentPointValue;

  // Check if values have changed from original
  const hasChanges = editValues.cardId !== editOptions.cardId ||
    editValues.condition !== editOptions.currentCondition ||
    editValues.language !== editOptions.currentLanguage ||
    editValues.foil !== editOptions.currentFoil;

  // Recalculate price when edit values change
  const recalculatePrice = useCallback(async (values: EditValues) => {
    if (!editOptions.wantListId) return;
    setIsCalculating(true);
    try {
      const result = await calculatePrice({
        cards: [{
          wantListId: editOptions.wantListId,
          cardId: values.cardId.toString(),
          condition: values.condition,
          language: values.language,
          foil: values.foil,
          set: values.setName,
        }]
      }).unwrap();
      const newPrice = result.cards.find(c => c.wantListId === editOptions.wantListId);
      if (newPrice) {
        setPreviewPrice(newPrice.displayPrice);
      }
    } catch {
      setPreviewPrice(null);
    } finally {
      setIsCalculating(false);
    }
  }, [calculatePrice, editOptions.wantListId]);

  // Trigger price recalculation when values change
  useEffect(() => {
    if (hasChanges) {
      recalculatePrice(editValues);
    } else {
      setPreviewPrice(null);
    }
  }, [editValues, hasChanges, recalculatePrice]);

  const updateField = (updates: Partial<EditValues>) => {
    setEditValues(prev => ({ ...prev, ...updates }));
  };

  // Handle set change
  const handleSetChange = (selectedValue: string) => {
    const selectedSetDetail = prefs.acceptableSetDetails.find(s => {
      const optionValue = s.cardId ? `${s.code}-${s.cardId}` : s.code;
      return optionValue === selectedValue;
    });
    if (!selectedSetDetail) return;

    const newAvailableInFoil = selectedSetDetail.availableInFoil ?? false;
    const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? true;

    const updates: Partial<EditValues> = {
      setCode: selectedSetDetail.code,
      setName: selectedSetDetail.name,
      cardId: selectedSetDetail.cardId,
      availableInFoil: newAvailableInFoil,
      availableInNonFoil: newAvailableInNonFoil,
    };

    if (selectedSetDetail.imageUrl) {
      updates.imageUrl = selectedSetDetail.imageUrl;
    }

    // Auto-switch foil if current selection becomes invalid for new printing
    const canOfferFoil = newAvailableInFoil && (prefs.acceptFoil ?? true);
    const canOfferNonFoil = newAvailableInNonFoil && (prefs.acceptNonFoil ?? true);

    if (editValues.foil && !canOfferFoil && canOfferNonFoil) {
      updates.foil = false;
    } else if (!editValues.foil && !canOfferNonFoil && canOfferFoil) {
      updates.foil = true;
    }

    updateField(updates);
  };

  const handleSave = () => {
    onSave(editOptions.itemId, {
      cardId: editValues.cardId,
      condition: editValues.condition,
      language: editValues.language,
      foil: editValues.foil,
    });
  };

  // Determine foil dropdown options
  const canOfferFoil = (editValues.availableInFoil) && (prefs.acceptFoil ?? true);
  const canOfferNonFoil = (editValues.availableInNonFoil) && (prefs.acceptNonFoil ?? true);
  const showFoilDropdown = canOfferFoil && canOfferNonFoil;

  // Filter conditions to acceptable
  const acceptableConditions = prefs.acceptableConditions?.length
    ? ALL_CONDITIONS.filter(c => prefs.acceptableConditions.includes(c.value))
    : ALL_CONDITIONS;

  // Build set dropdown value
  const currentSetValue = editValues.cardId
    ? `${editValues.setCode}-${editValues.cardId}`
    : editValues.setCode;

  // Calculate price delta
  const displayPrice = previewPrice ?? originalPrice;
  const perCardDelta = hasChanges && previewPrice !== null ? previewPrice - originalPrice : 0;
  const totalDelta = perCardDelta * item.qty;

  return (
    <div className="table-row editing">
      <span className="col-qty">{item.qty}</span>

      <div className="col-name">
        <FoilCardImage
          src={AssetUrls.cards.ensureCDNUrl(editValues.imageUrl)?.replace('/large/', '/small/')?.replace('/normal/', '/small/') || ''}
          alt={item.name}
          className="card-thumbnail"
          foil={editValues.foil}
          borderRadius="4px"
        />
        <span>{item.name}</span>
      </div>

      {/* Set dropdown */}
      <div className="col-set">
        {prefs.acceptableSetDetails.length > 1 ? (
          <select
            value={currentSetValue}
            onChange={(e) => handleSetChange(e.target.value)}
            className="edit-select"
            disabled={isSaving}
          >
            {[...prefs.acceptableSetDetails]
              .sort((a, b) => {
                const nameCompare = a.name.localeCompare(b.name);
                if (nameCompare !== 0) return nameCompare;
                const aNum = parseInt(a.collectorNumber || '0', 10);
                const bNum = parseInt(b.collectorNumber || '0', 10);
                return aNum - bNum;
              })
              .map(setDetail => {
                const uniqueValue = setDetail.cardId
                  ? `${setDetail.code}-${setDetail.cardId}`
                  : setDetail.code;
                const displayText = setDetail.collectorNumber
                  ? `${setDetail.name} #${setDetail.collectorNumber}`
                  : setDetail.name;
                return (
                  <option key={uniqueValue} value={uniqueValue}>
                    {displayText}
                  </option>
                );
              })}
          </select>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <SetIcon setIconUrl={item.setIconUrl} setName={editValues.setName} />
            <span>{editValues.setCode || editValues.setName}</span>
          </div>
        )}
      </div>

      {/* Foil dropdown */}
      <span className="col-foil">
        {showFoilDropdown ? (
          <select
            value={editValues.foil ? 'foil' : 'non-foil'}
            onChange={(e) => updateField({ foil: e.target.value === 'foil' })}
            className="edit-select"
            disabled={isSaving}
          >
            <option value="non-foil">—</option>
            <option value="foil">Foil</option>
          </select>
        ) : (
          canOfferFoil ? 'Foil' : '—'
        )}
      </span>

      {/* Condition dropdown */}
      <span className="col-condition">
        {acceptableConditions.length > 1 ? (
          <select
            value={editValues.condition}
            onChange={(e) => updateField({ condition: e.target.value })}
            className="edit-select"
            disabled={isSaving}
          >
            {acceptableConditions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          formatCondition(editValues.condition)
        )}
      </span>

      {/* Language dropdown */}
      <span className="col-language">
        {prefs.acceptableLanguages?.length > 1 ? (
          <select
            value={editValues.language}
            onChange={(e) => updateField({ language: e.target.value })}
            className="edit-select"
            disabled={isSaving}
          >
            {prefs.acceptableLanguages.map(lang => (
              <option key={lang} value={lang}>{formatLanguage(lang)}</option>
            ))}
          </select>
        ) : (
          formatLanguage(editValues.language)
        )}
      </span>

      {/* Points with delta */}
      <div className="col-points">
        {hasChanges && previewPrice !== null ? (
          <div className="price-delta">
            <span className="price-original">
              <Points points={originalPrice * item.qty} type="small" />
            </span>
            <Points points={displayPrice * item.qty} type="small" />
            {totalDelta !== 0 && (
              <span className={`price-delta-indicator ${totalDelta > 0 ? 'positive' : 'negative'}`}>
                {totalDelta > 0 ? '+' : ''}{totalDelta}
                {item.qty > 1 && ` (${perCardDelta > 0 ? '+' : ''}${perCardDelta}/ea)`}
              </span>
            )}
          </div>
        ) : isCalculating ? (
          <span className="price-calculating">...</span>
        ) : (
          <Points points={originalPrice * item.qty} type="small" />
        )}
      </div>

      {/* Save/Cancel actions */}
      <div className="col-edit edit-actions">
        <button
          className="edit-action-btn save"
          onClick={handleSave}
          disabled={isSaving || isCalculating || !hasChanges}
          title="Save changes"
        >
          {isSaving ? (
            <span className="edit-spinner" />
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
            </svg>
          )}
        </button>
        <button
          className="edit-action-btn cancel"
          onClick={onCancel}
          disabled={isSaving}
          title="Cancel edit"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z"/>
          </svg>
        </button>
      </div>

      {/* Empty remove column (actions replace it) */}
      <div className="col-remove" />
    </div>
  );
}
